import { LenderConfiguration } from '@shared/model/lender-config.model';
import { Component, OnInit, inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingUrl } from '@shared/constant/route-const';
import { Params, TargetType } from '@shared/constant/target-flow-const';
import { StatefulService } from '@app/service/stateful.service';
import { HostPrefillService } from '@workflow/ecp-workflow/dynamic-app/service/host-prefill.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { CUDLConnect } from '@workflow/ecp-workflow/dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { AppConfigService as ConfigService, TokenService } from '@cudirect/shared-ui-library';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { StorageService } from '@workflow/service/storage/storage.service';
import { UserSessionService } from '@shared/service/user-session.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { CommonModule , isPlatformBrowser } from '@angular/common';
import { WorkflowPageNavigationStateService } from '@workflow/service/workflow-page-navigation-state/workflow-page-navigation-state.service';
import { PrefillAuthenticationService } from '@workflow/service/prefill-authentication/prefill-authentication.service';

/**
 *
 * Redirect UI Binding
 */
@Component({
  selector: 'cu-redirect',
  templateUrl: './redirect.component.html',
  standalone: true,
  imports: [CommonModule]
})
export class RedirectComponent implements OnInit {
  /** BS Model Ref */
  public lenderData: LenderConfiguration;
  private target = '';
  private flowType = '';
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  // Inject dependencies using the inject pattern
  private readonly router = inject(Router);
  public readonly activeRoute = inject(ActivatedRoute);
  private readonly statefulService = inject(StatefulService);
  private readonly hostPrefillService = inject(HostPrefillService);
  private readonly tokenService = inject(TokenService);
  private readonly userSession = inject(UserSessionService);
  private readonly pageNavigationStateService = inject(WorkflowPageNavigationStateService);
  private readonly configService = inject(ConfigService);
  private readonly xApiKeyService = inject(XApiKeyService);
  public readonly storageService = inject(StorageService);
  public readonly userService = inject(UserService);
  public readonly l360Service = inject(L360Service);
  private readonly bundleHelperService = inject(BundleHelperService);
  private readonly googleTrackingService = inject(GoogleTrackingService);
  private readonly logger = inject(SawDust);
  private readonly prefillAuthService = inject(PrefillAuthenticationService);
  
  private logContext(additional: Record<string, unknown> = {}) {
    return { component: 'RedirectComponent', flowType: this.flowType, target: this.target, ...additional };
  }

  public ngOnInit(): void {
    // Redirect based on target value : targetflow={{target}}
    this.activeRoute.queryParams.subscribe((params) => {
      this.target = params[Params.target]?.toLowerCase().substring(0, 3);
      const ola = this.target === TargetType.OLA;
      const protocol = ola ? 'ola' : 'api';
      this.flowType = ola ? 'OLA' : 'OAO';
      const isHostPrefillCompleted = this.statefulService.wrap(`${protocol}:isHostPrefillCompleted`).value as boolean;
      const ssisHostPrefillCompleted = this.statefulService.wrap('ss:isHostPrefillCompleted').value;
      this.logger.debug('Processing redirect parameters', this.logContext({ hasOtuToken: !!params[Params.token], hostPrefillCompleted: isHostPrefillCompleted, sessionPrefillCompleted: ssisHostPrefillCompleted }));
      this.saveOtuTokenToBundle(ola, params[Params.token]);
      if (!ssisHostPrefillCompleted && !isHostPrefillCompleted) {
        this.prefillData(this.target === TargetType.OLA, params[Params.token])
          .then(() => {
            this.logger.info('Prefill sequence completed successfully', this.logContext());
            this.navigateNext();
          })
          .catch(() => {
            this.logger.warn('Prefill sequence failed; continuing navigation', this.logContext());
            this.navigateNext();
          });
      }
    });
  }

  private navigateNext() {
    if (this.target === TargetType.OLA) {
      // Persist the prefill params while removing some of the ones when navigating to OLA
      this.router.navigate([RoutingUrl.OLA.LoanType], {
        queryParams: {
          otutoken: null,
          target: null,
          frame: null
        },
        queryParamsHandling: 'merge'
      });
      this.logger.info('Navigated to OLA loan type selection', this.logContext());
    }
    if (this.target === TargetType.OAO) {
      // Why do we need a fresh loan flag in session storage? What is this powering?
      this.statefulService.wrap('ss:oao-fresh-loan').value = true;
      // Persist the prefill params while removing some of the ones when navigating to OLA
      this.router.navigate([RoutingUrl.OAO.Membership], {
        queryParams: {
          otutoken: null,
          target: null,
          frame: null
        },
        queryParamsHandling: 'merge'
      });
      this.logger.info('Navigated to OAO membership flow', this.logContext());
    }
  }

  // Why do we needthe otutoken if its one time use? Is it for debugging? Why does the bundle need this here?
  public saveOtuTokenToBundle(isOla: boolean, otutoken: string): void {
    const protocol = isOla ? 'ola' : 'api';
    this.statefulService.wrap(`${protocol}:hostPrefillToken`).value = otutoken;

    if (isOla) {
      this.statefulService.wrap('ola:olaApplicationInformation/membership/isMember').value = true;
    }
    this.logger.debug('Stored OTU token in bundle', this.logContext({ isOla, tokenPresent: !!otutoken }));
  }


  /**
   * Method to get the prefill data by calling the API
   * @param isOla determine whether the flow is OLA or not
   * @param otuToken current token
   */
  public prefillData(isOla: boolean, otuToken: string): Promise<void> {
    this.hostPrefillService.flowId = isOla ? 'ola' : 'oao';
    this.logger.info('Starting prefill data retrieval', this.logContext({ isOla, tokenPresent: !!otuToken }));

    const promise = isOla ? this.bundleHelperService.refreshOLABundleAsync() : this.bundleHelperService.refreshBundleAsync();
    return promise
      .then(() => {
        return this.hostPrefillService.getHbData(otuToken).then((res?: CUDLConnect) => {
          if (!res) {
            this.logger.warn('No prefill data received', this.logContext());
            return;
          }
          
          // Delegate to PrefillAuthenticationService
          return this.prefillAuthService.executePrefillAuthenticationFlow(res, isOla, 'Homebanking Redirect')
            .then((result) => {
              if (!result.success) {
                this.logger.warn('Prefill authentication flow did not complete successfully', this.logContext());
              }
            });
        });
      })
      .catch((err) => {
        let gaActionName = 'arcdx.';
        if (this.flowType === 'OLA') {
          gaActionName += 'loans.';
        } else {
          gaActionName += 'accounts.';
        }
        gaActionName += 'prefill-error';
        this.googleTrackingService.publishEvent(gaActionName, {
          Page: 'Homebanking Redirect',
          ApplicationType: this.flowType
        });
        this.logger.error('Error during prefill data retrieval', err, this.logContext());
        throw err;
      });
  }
}
